import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { api as axios, API_BASE_URL } from '../utils/apiClient';
import { fetchBackupsForSite, requestBackup, triggerBackupCleanup } from '../api/backupService';
import './BackupRestore.css';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / 1024 ** i;
  return `${value.toFixed(1)} ${sizes[i]}`;
};

const BackupRestore = () => {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [backups, setBackups] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [message, setMessage] = useState(null);
  const [driveFolderLink, setDriveFolderLink] = useState('');
  const [savingDriveLink, setSavingDriveLink] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [driveOAuthToken, setDriveOAuthToken] = useState(null);
  const [connectingDrive, setConnectingDrive] = useState(false);

  const loadSites = async () => {
    setLoadingSites(true);
    try {
      const response = await axios.get('/api/sites');
      const siteList = response.data || [];
      setSites(siteList);
      if (siteList.length && !selectedSiteId) {
        setSelectedSiteId(siteList[0]._id);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to load sites.' });
    } finally {
      setLoadingSites(false);
    }
  };

  const loadBackups = async (siteId) => {
    if (!siteId) {
      setBackups([]);
      return;
    }
    setLoadingBackups(true);
    try {
      const response = await fetchBackupsForSite(siteId);
      setBackups(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Unable to load backups.' });
    } finally {
      setLoadingBackups(false);
    }
  };

  useEffect(() => {
    loadSites();
    // Trigger automatic cleanup when backup page loads
    triggerBackupCleanup().catch(err => {
      console.warn('[BackupRestore] Cleanup trigger failed (non-critical):', err);
    });
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadBackups(selectedSiteId);
      loadSiteDetails(selectedSiteId);
    }
  }, [selectedSiteId]);

  const loadSiteDetails = async (siteId) => {
    try {
      const response = await axios.get(`/api/sites/${siteId}`);
      const site = response.data;
      setCurrentSite(site);
      if (site.backupStorageLocation?.driveFolderLink) {
        setDriveFolderLink(site.backupStorageLocation.driveFolderLink);
      } else {
        setDriveFolderLink('');
      }
      setDriveOAuthToken(site.backupStorageLocation?.driveOAuthToken || null);
    } catch (error) {
      console.error('[BackupRestore] Failed to load site details:', error);
    }
  };

  const handleSaveDriveLink = async () => {
    if (!selectedSiteId) return;

    // Validate Google Drive folder URL (allow query parameters)
    const trimmedLink = driveFolderLink.trim();
    if (trimmedLink) {
      // More flexible pattern that allows query parameters
      const driveUrlPattern = /^https:\/\/drive\.google\.com\/drive\/folders\/[a-zA-Z0-9_-]+/;
      if (!driveUrlPattern.test(trimmedLink)) {
        setMessage({
          type: 'error',
          text: 'Invalid Google Drive folder URL. Please provide a valid Google Drive folder link.',
        });
        return;
      }
    }

    setSavingDriveLink(true);
    setMessage(null);

    try {
      const response = await axios.put(`/api/sites/${selectedSiteId}/backup-storage-location`, {
        driveFolderLink: trimmedLink || null,
      });

      setMessage({
        type: 'success',
        text: response.data.message || 'Backup storage location saved successfully.',
      });

      // Reload site details to get updated storage location
      await loadSiteDetails(selectedSiteId);
    } catch (error) {
      console.error('[BackupRestore] Error saving drive link:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save backup storage location.';
      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setSavingDriveLink(false);
    }
  };

  const handleConnectGoogleDrive = async () => {
    if (!selectedSiteId) return;
    
    setConnectingDrive(true);
    try {
      const response = await axios.get(`/api/drive-oauth/auth-url/${selectedSiteId}`);
      if (response.data.success && response.data.authUrl) {
        // Redirect to Google OAuth
        window.location.href = response.data.authUrl;
      } else {
        const errorMsg = response.data?.message || 'Failed to generate OAuth URL.';
        setMessage({
          type: 'error',
          text: errorMsg,
        });
        setConnectingDrive(false);
      }
    } catch (error) {
      console.error('[BackupRestore] Error connecting Google Drive:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to connect Google Drive. Please check server configuration.';
      setMessage({
        type: 'error',
        text: errorMsg,
      });
      setConnectingDrive(false);
    }
  };

  // Check for OAuth callback parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const driveConnected = urlParams.get('driveConnected');
    const driveError = urlParams.get('driveError');
    const siteId = urlParams.get('siteId');
    
    if (driveConnected === 'true' && siteId) {
      setMessage({
        type: 'success',
        text: 'Google Drive connected successfully! Backups will now be uploaded to Drive.',
      });
      // Reload site details to get updated OAuth token
      loadSiteDetails(siteId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (driveError) {
      setMessage({
        type: 'error',
        text: `Failed to connect Google Drive: ${decodeURIComponent(driveError)}`,
      });
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Download blob as file
   * Creates a temporary download link and triggers browser download
   */
  const downloadBlob = (blob, filename) => {
    console.log('[Frontend] downloadBlob called with filename:', filename);
    console.log('[Frontend] Blob size:', blob.size, 'bytes');
    
    if (!blob || blob.size === 0) {
      console.error('[Frontend] ❌ Cannot download empty blob');
      throw new Error('File is empty or invalid');
    }
    
    try {
      const url = window.URL.createObjectURL(blob);
      console.log('[Frontend] Created object URL:', url);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      console.log('[Frontend] Appending link to DOM...');
      document.body.appendChild(link);
      
      console.log('[Frontend] Triggering click...');
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('[Frontend] ✅ Download link cleaned up');
      }, 100);
      
      console.log('[Frontend] ✅ Download triggered successfully');
    } catch (err) {
      console.error('[Frontend] ❌ Error in downloadBlob:', err);
      throw err;
    }
  };

  /**
   * Download Files Backup
   * If backup is stored in Google Drive, redirects to the Drive file/folder link stored in the backup record.
   * Otherwise, downloads via the backend API.
   */
  const handleDownloadFiles = async (backup) => {
    console.log('[Frontend] ===== Starting files backup download =====');
    
    // Check if backup is stored in Google Drive and has Drive links
    const googleDrive = backup.googleDrive;
    const storageType = backup.storageType || 'local';
    
    // If backup is in Drive, redirect to the stored Drive link
    if (storageType === 'google-drive' && googleDrive) {
      // Prefer direct file link if available
      if (googleDrive.filesZipLink) {
        console.log('[Frontend] Backup stored in Google Drive - redirecting to file link');
        console.log('[Frontend] Drive file link:', googleDrive.filesZipLink);
        window.open(googleDrive.filesZipLink, '_blank');
        setMessage({
          type: 'success',
          text: 'Opening Google Drive file...',
        });
        return;
      }
      
      // Fallback to Drive folder link if file link not available
      if (googleDrive.driveFolderLink) {
        console.log('[Frontend] Backup stored in Google Drive - redirecting to folder link');
        console.log('[Frontend] Drive folder link:', googleDrive.driveFolderLink);
        window.open(googleDrive.driveFolderLink, '_blank');
        setMessage({
          type: 'success',
          text: 'Opening Google Drive folder...',
        });
        return;
      }
      
      // If Drive storage but no links, show error
      setMessage({
        type: 'error',
        text: 'Backup is stored in Google Drive but Drive links are not available. Please check the backup record.',
      });
      return;
    }
    
    // Local storage - use download API
    // Extract siteId and timestamp
    let siteId = backup.siteId;
    if (!siteId && selectedSiteId) {
      siteId = selectedSiteId;
    }
    if (!siteId && backup.downloadFilesUrl) {
      const match = backup.downloadFilesUrl.match(/\/api\/download\/([^/]+)\//);
      if (match) siteId = match[1];
    }
    
    let timestamp = backup.timestamp;
    if (!timestamp && backup.downloadFilesUrl) {
      const match = backup.downloadFilesUrl.match(/\/api\/download\/[^/]+\/([^/]+)\//);
      if (match) timestamp = match[1];
    }
    if (!timestamp && backup.folder) {
      const match = backup.folder.match(/\/([^/]+)\/?$/);
      if (match) timestamp = match[1];
    }
    
    if (!siteId || !timestamp) {
      setMessage({
        type: 'error',
        text: 'Invalid backup data. Please refresh the page and try again.',
      });
      return;
    }
    
    const downloadUrl = `${API_BASE_URL}/api/download/${siteId}/${timestamp}/files`;
    console.log('[Frontend] Download URL:', downloadUrl);
    
    try {
      // Let the browser handle the streaming download directly. This avoids
      // buffering the whole file in JavaScript (which is slower and uses more memory)
      // and generally results in faster, more reliable downloads.
      setMessage({
        type: 'info',
        text: 'Starting files backup download. Your browser will handle the download.',
      });

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'files.zip';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('[Frontend] ❌ Download failed:', error);
      const errorMessage = error.message || 'Unable to download file backup.';
      setMessage({
        type: 'error',
        text: `Download failed: ${errorMessage}`,
      });
    }
  };

  /**
   * Download Database Backup
   * If backup is stored in Google Drive, redirects to the Drive file/folder link stored in the backup record.
   * Otherwise, downloads via the backend API.
   */
  const handleDownloadDb = async (backup) => {
    console.log('[Frontend] ===== Starting database backup download =====');
    
    // Check if backup is stored in Google Drive and has Drive links
    const googleDrive = backup.googleDrive;
    const storageType = backup.storageType || 'local';
    
    // If backup is in Drive, redirect to the stored Drive link
    if (storageType === 'google-drive' && googleDrive) {
      // Prefer direct file link if available
      if (googleDrive.dbSqlLink) {
        console.log('[Frontend] Backup stored in Google Drive - redirecting to file link');
        console.log('[Frontend] Drive file link:', googleDrive.dbSqlLink);
        window.open(googleDrive.dbSqlLink, '_blank');
        setMessage({
          type: 'success',
          text: 'Opening Google Drive file...',
        });
        return;
      }
      
      // Fallback to Drive folder link if file link not available
      if (googleDrive.driveFolderLink) {
        console.log('[Frontend] Backup stored in Google Drive - redirecting to folder link');
        console.log('[Frontend] Drive folder link:', googleDrive.driveFolderLink);
        window.open(googleDrive.driveFolderLink, '_blank');
        setMessage({
          type: 'success',
          text: 'Opening Google Drive folder...',
        });
        return;
      }
      
      // If Drive storage but no links, show error
      setMessage({
        type: 'error',
        text: 'Backup is stored in Google Drive but Drive links are not available. Please check the backup record.',
      });
      return;
    }
    
    // Local storage - use download API
    // Extract siteId and timestamp
    let siteId = backup.siteId;
    if (!siteId && selectedSiteId) {
      siteId = selectedSiteId;
    }
    if (!siteId && backup.downloadDbUrl) {
      const match = backup.downloadDbUrl.match(/\/api\/download\/([^/]+)\//);
      if (match) siteId = match[1];
    }
    if (!siteId && backup.downloadFilesUrl) {
      const match = backup.downloadFilesUrl.match(/\/api\/download\/([^/]+)\//);
      if (match) siteId = match[1];
    }
    
    let timestamp = backup.timestamp;
    if (!timestamp && backup.downloadDbUrl) {
      const match = backup.downloadDbUrl.match(/\/api\/download\/[^/]+\/([^/]+)\//);
      if (match) timestamp = match[1];
    }
    if (!timestamp && backup.downloadFilesUrl) {
      const match = backup.downloadFilesUrl.match(/\/api\/download\/[^/]+\/([^/]+)\//);
      if (match) timestamp = match[1];
    }
    if (!timestamp && backup.folder) {
      const match = backup.folder.match(/\/([^/]+)\/?$/);
      if (match) timestamp = match[1];
    }
    
    if (!siteId || !timestamp) {
      setMessage({
        type: 'error',
        text: 'Invalid backup data. Please refresh the page and try again.',
      });
      return;
    }
    
    const downloadUrl = `${API_BASE_URL}/api/download/${siteId}/${timestamp}/db`;
    console.log('[Frontend] Download URL:', downloadUrl);
    
    try {
      setMessage({
        type: 'info',
        text: 'Starting database backup download. Your browser will handle the download.',
      });

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'database.sql';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('[Frontend] ❌ Download failed:', error);
      const errorMessage = error.message || 'Unable to download database backup.';
      setMessage({
        type: 'error',
        text: `Download failed: ${errorMessage}`,
      });
    }
  };

  /**
   * Create Backup (Automatically uploads to Google Drive)
   * API: POST /api/backup-request/:siteId
   * This triggers: WordPress backup → Download to server → Upload to Google Drive → Delete local files
   */
  const handleBackupNow = async () => {
    if (!selectedSiteId) return;
    
    console.log('[Frontend] ===== Starting backup creation =====');
    console.log('[Frontend] Site ID:', selectedSiteId);
    
    setCreatingBackup(true);
    setMessage(null);
    
    try {
      console.log('[Frontend] Step 1: Sending backup request to backend...');
      const response = await requestBackup(selectedSiteId);
      
      console.log('[Frontend] Step 2: Backup request completed');
      console.log('[Frontend] Response:', response.data);
      
      const backupData = response.data?.data || response.data;
      const storageType = backupData.storageType || 'local';
      const googleDrive = backupData.googleDrive;
      const driveUploadSuccess = backupData.driveUploadSuccess;
      const driveFolderLink = backupData.driveFolderLink;
      const message = backupData.message;
      
      console.log('[Frontend] Storage Type:', storageType);
      console.log('[Frontend] Drive Upload Success:', driveUploadSuccess);
      
      // Show message from backend
      if (message) {
        setMessage({ 
          type: 'success', 
          text: message 
        });
      } else {
        // Fallback message
        setMessage({ 
          type: 'success', 
          text: 'Backup created and saved locally.' 
        });
      }
      
      console.log('[Frontend] Step 3: Reloading backups list...');
      await loadBackups(selectedSiteId);
      console.log('[Frontend] ✅ Backup creation completed');
      console.log('[Frontend] ===== Backup process finished =====');
      
    } catch (error) {
      console.error('[Frontend] ❌ Backup creation failed (or timed out)');
      console.error('[Frontend] Error:', error);
      console.error('[Frontend] ===== Backup error handler =====');

      // If the request timed out, assume the backup is still running on the
      // server and let the user know it will appear in the list once ready.
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        setMessage({
          type: 'info',
          text: 'Backup is still running in the background. You can continue using the app. Once the backup is moved to Google Drive it will appear in "Previous Backups" and you will be able to download it.',
        });
        return;
      }

      const errorData = error.response?.data || {};

      // Start with the most helpful high-level message we have
      let errorText =
        errorData.message ||
        errorData.error ||
        'Backup failed. Please check the site logs.';

      // If the value is an object, unwrap it instead of showing [object Object]
      if (typeof errorText === 'object' && errorText !== null) {
        if (errorText.message) {
          errorText = errorText.message;
        } else {
          errorText = JSON.stringify(errorText);
        }
      }
      
      // Add details if available
      if (errorData.details) {
        const detailsText =
          typeof errorData.details === 'string'
            ? errorData.details
            : JSON.stringify(errorData.details);
        errorText += ` ${detailsText}`;
      }
      
      // Add WordPress status code if available
      if (errorData.wpStatus) {
        errorText += ` (WordPress returned status ${errorData.wpStatus})`;
      }
      
      // Add specific guidance for common errors
      if (error.response?.status === 400 && errorText.includes('plugin key')) {
        errorText += ' Go to "Add Website" to configure the plugin key.';
      } else if (error.response?.status === 502) {
        if (errorText.includes('Authentication') || errorData.wpStatus === 401) {
          errorText += ' Verify the plugin key in your WordPress site settings matches the one in this application.';
        } else if (errorText.includes('endpoint not found') || errorData.wpStatus === 404) {
          errorText += ' Make sure the WebMonitor plugin is installed and activated on your WordPress site.';
        } else if (errorText.includes('Cannot connect')) {
          errorText += ' Check if the WordPress site is accessible and the URL is correct.';
        } else {
          // Generic 502 guidance
          errorText += ' Check your backend server logs for more details. The WordPress site may be unreachable or the plugin may not be configured correctly.';
        }
      } else if (error.response?.status === 504) {
        errorText += ' The WordPress site may be slow or unresponsive. Try again later.';
      }
      
      // Log full error for debugging
      console.error('[Backup] Error details:', {
        status: error.response?.status,
        error: errorData,
        fullError: error,
      });
      
      setMessage({ type: 'error', text: errorText });
    } finally {
      setCreatingBackup(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div>
            <p className="eyebrow">Integrations</p>
            <h1>Backup</h1>
            <p className="subheading">Create and download backups from your connected WordPress sites.</p>
          </div>
        </div>

        {message && (
          <div className={`backup-alert ${message.type}`}>
            <strong>
              {message.type === 'success'
                ? 'Success'
                : message.type === 'info'
                ? 'Info'
                : 'Error'}
              :
            </strong>{' '}
            {message.text}
            <button type="button" onClick={() => setMessage(null)}>
              ×
            </button>
          </div>
        )}

        <div className="backup-controls">
          <div className="backup-site-select">
            <label htmlFor="site-select">Select Website</label>
            {loadingSites ? (
              <p>Loading sites…</p>
            ) : (
              <select
                id="site-select"
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
              >
                {sites.map((site) => (
                  <option key={site._id} value={site._id}>
                    {site.name} ({site.url})
                  </option>
                ))}
              </select>
            )}
          </div>
          <button
            type="button"
            className="primary-btn"
            onClick={handleBackupNow}
            disabled={!selectedSiteId || creatingBackup}
          >
            {creatingBackup ? 'Creating Backup…' : 'Backup Now'}
          </button>
        </div>

        <div className="backup-storage-location">
          <div className="backup-storage-input">
            <label htmlFor="drive-folder-link">Backup Storage Location</label>
            <input
              id="drive-folder-link"
              type="text"
              placeholder="Paste your Google Drive folder link"
              value={driveFolderLink}
              onChange={(e) => setDriveFolderLink(e.target.value)}
              disabled={!selectedSiteId || savingDriveLink}
            />
            <p className="backup-storage-info">
              Paste your Google Drive folder link. All future backups for this site will be saved to that folder.
              {driveFolderLink && !driveOAuthToken && (
                <span className="drive-oauth-warning">
                  {' '}⚠️ Drive folder configured but not connected. Click "Connect Google Drive" to enable uploads.
                </span>
              )}
              {driveOAuthToken && (
                <span className="drive-oauth-success">
                  {' '}✅ Google Drive connected. Backups will be uploaded automatically.
                </span>
              )}
            </p>
          </div>
          <div className="backup-storage-actions">
            <button
              type="button"
              className="primary-btn"
              onClick={handleSaveDriveLink}
              disabled={!selectedSiteId || savingDriveLink}
            >
              {savingDriveLink ? 'Saving…' : 'Save'}
            </button>
            {driveFolderLink && !driveOAuthToken && (
              <button
                type="button"
                className="primary-btn drive-connect-btn"
                onClick={handleConnectGoogleDrive}
                disabled={!selectedSiteId || connectingDrive}
              >
                {connectingDrive ? 'Connecting…' : 'Connect Google Drive'}
              </button>
            )}
          </div>
        </div>

        <section className="connected-section">
          <div className="connected-header">
            <div>
              <h2>Previous Backups</h2>
              <p>Download file and database backups for the selected site.</p>
            </div>
          </div>

          {loadingBackups ? (
            <div className="empty-state">Loading backups…</div>
          ) : backups.length === 0 ? (
            <div className="empty-state">
              <p>No backups found for this site yet.</p>
              <p className="muted">Click “Backup Now” to create the first backup.</p>
            </div>
          ) : (
            <div className="connected-list">
              <table className="backup-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>File Backup</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Database Backup</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Storage Type</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Drive Status</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup) => {
                    const date = backup.createdAt ? new Date(backup.createdAt) : null;
                    const dateText = date ? date.toLocaleString() : 'N/A';
                    const filesSize = backup.size?.files ?? backup.sizeFiles ?? backup.fileSize;
                    const dbSize = backup.size?.db ?? backup.sizeDb ?? backup.dbSize;
                    const storageType = backup.storageType || 'local';
                    const driveStatus = backup.googleDrive?.uploadStatus || (storageType === 'google-drive' ? 'success' : null);
                    const driveError = backup.googleDrive?.uploadError;
                    
                    return (
                      <tr key={`${backup.folder}-${backup.createdAt}`}>
                        <td style={{ padding: '8px' }}>{dateText}</td>
                        <td style={{ padding: '8px' }}>{formatBytes(filesSize)}</td>
                        <td style={{ padding: '8px' }}>{formatBytes(dbSize)}</td>
                        <td style={{ padding: '8px' }}>
                          <span className={`storage-badge ${storageType === 'google-drive' ? 'storage-drive' : 'storage-local'}`}>
                            {storageType === 'google-drive' ? 'Drive' : 'Local'}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          {driveStatus ? (
                            <span className={`drive-status ${driveStatus === 'success' ? 'status-success' : driveStatus === 'failed' ? 'status-failed' : 'status-pending'}`}>
                              {driveStatus === 'success' ? 'Success' : driveStatus === 'failed' ? 'Failed' : 'Pending'}
                              {driveError && (
                                <span className="drive-error" title={driveError}>
                                  {' '}({driveError.length > 60 ? driveError.substring(0, 60) + '...' : driveError})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {filesSize > 0 ? (
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => handleDownloadFiles(backup)}
                            >
                              Download Files
                            </button>
                          ) : (
                            <span className="muted">Files backup not available</span>
                          )}
                          {' | '}
                          {dbSize > 0 ? (
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => handleDownloadDb(backup)}
                            >
                              Download DB
                            </button>
                          ) : (
                            <span className="muted">DB backup not available</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

    </div>
  );
};

export default BackupRestore;



